/*
 * @Description: 个人轨迹回放视图（实时轨迹页的子视图，同路由 /member/track + query 驱动）
 *   顶部：返回 + 日期；中部地图（节点+轨迹）；右侧「成员轨迹」面板。
 *   完全由 URL props（userId/date）驱动：切换成员/日期 = 改 query，刷新/深链接保留。
 *   A 期为静态可视；底部播放控件（播放/倍速/跳过）为 B 期。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, DatePicker, Layout, Tag } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useGroupID } from '../../../app/hooks'
import { CockpitMapStyle, isCockpitMockEnabled } from '../constants'
import { useRealtimeMembers } from '../hooks/useRealtimeMembers'
import { useMemberDetail } from '../hooks/useMemberDetail'
import { useTrackTimeline } from '../hooks/useTrackTimeline'
import { useTrackStats } from '../hooks/useTrackStats'
import { usePoiList } from '../hooks/usePoiList'
import { getTrackRange } from './utils/playback'
import ReplayMap, { ReplayMapRef } from './components/ReplayMap'
import TrackSidePanel from './components/TrackSidePanel'
import ReplayControl from './components/ReplayControl'
import MapStyleSelect from '../components/MapStyleSelect'
import PoiManager from '../components/PoiManager'
import LocationSettings from '../components/LocationSettings'
import '../index.scss'
import './index.scss'

const { Content } = Layout

/** 1x 下整段轨迹播放约 12 秒（参考 aiWeb）；倍速线性缩放 */
const BASE_PLAY_MS = 12 * 1000
/** 经过起/停/拍照节点时的停顿时长 */
const NODE_PAUSE_MS = 1500

interface TrackReplayProps {
  /** 回放成员 userId（来自 query） */
  userId: string
  /** 回放日期 YYYY-MM-DD（来自 query，空=今天） */
  date: string
  /** 地图样式（与驾驶舱共用，由父级持有并持久化） */
  mapStyle: CockpitMapStyle
  /** 地图样式变化 */
  onChangeMapStyle: (style: CockpitMapStyle) => void
}

/** 跳到回放视图（切成员/日期用 replace，不堆历史） */
function replaceReplay(
  navigate: (path: string, options?: { replace?: boolean }) => void,
  userId: string,
  dateStr: string
) {
  navigate(
    `/member/track?replayUserId=${encodeURIComponent(
      userId
    )}&replayDate=${dateStr}`,
    { replace: true }
  )
}

const TrackReplay: React.FC<TrackReplayProps> = ({
  userId,
  date,
  mapStyle,
  onChangeMapStyle
}) => {
  const groupId = useGroupID()
  const navigate = useNavigate()
  const isMock = isCockpitMockEnabled()
  const mapRef = useRef<ReplayMapRef>(null)
  // 「管理点位」弹窗开关
  const [poiOpen, setPoiOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // 成员轨迹面板是否收起（收起后只剩「轨迹」标签，底部浮条向右延伸）
  const [sideCollapsed, setSideCollapsed] = useState(false)

  const dateMoment = useMemo(
    () => (date ? dayjs(date, 'YYYY-MM-DD') : dayjs()),
    [date]
  )
  const dateStr = dateMoment.format('YYYY-MM-DD')
  const startSec = useMemo(
    () => dateMoment.clone().startOf('day').unix(),
    [dateMoment]
  )
  const endSec = useMemo(
    () => dateMoment.clone().endOf('day').unix(),
    [dateMoment]
  )

  // 回放页成员下拉只需一次性列表，不轮询（传 false）
  // 回放页只用成员下拉，一次拉全（pageSize 100）
  const { members } = useRealtimeMembers(groupId, dateStr, false, 100)
  // 地图常驻展示已保存的点位（与驾驶舱一致），管理弹窗增删后刷新
  const { pois: mapPois, refresh: refreshPois } = usePoiList(groupId, '', true)
  const { detail } = useMemberDetail(groupId, userId)
  const { timeline } = useTrackTimeline(groupId, userId, dateStr)
  const { stats } = useTrackStats(groupId, userId, startSec, endSec)

  const memberOptions = useMemo(
    () => members.map((m) => ({ userId: m.userId, name: m.name })),
    [members]
  )

  // 回放时间范围 + 播放状态
  const { start: trackStart, end: trackEnd } = useMemo(
    () => getTrackRange(timeline),
    [timeline]
  )
  // 节点时刻（起/停/拍照/当前），播放经过时停顿
  const nodeTimes = useMemo(
    () =>
      (timeline?.events ?? [])
        .filter((e) => e.type !== 'move')
        .map((e) => (e.type === 'move' ? 0 : e.time))
        .sort((a, b) => a - b),
    [timeline]
  )
  // 「起点处」要依次展示卡片的节点序号（与 ReplayMap 的 nodes 同口径=非 move 事件下标）：
  // 起点 + 与起点同刻的后续节点（如同地点同时刻的首个停留点）
  const introNodeIndices = useMemo<number[]>(() => {
    const nodeEvents = (timeline?.events ?? []).filter((e) => e.type !== 'move')
    if (!nodeEvents.length) return []
    const timeOf = (i: number) =>
      nodeEvents[i].type === 'move'
        ? 0
        : (nodeEvents[i] as { time: number }).time
    const t0 = timeOf(0)
    const out: number[] = []
    for (let i = 0; i < nodeEvents.length; i++) {
      if (timeOf(i) === t0) out.push(i)
      else break
    }
    return out
  }, [timeline])
  const [playTime, setPlayTime] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [speed, setSpeed] = useState(2)

  const playTimeRef = useRef(0)
  playTimeRef.current = playTime
  const playingRef = useRef(playing)
  playingRef.current = playing
  // 拖动进度条状态：是否拖动中（拖动即暂停、松手保持暂停）
  const scrubbingRef = useRef(false)
  // 已停顿过的节点 + 停顿截止时刻（performance.now）
  const passedNodesRef = useRef<Set<number>>(new Set())
  const pauseUntilRef = useRef(0)
  // 「进入序列」定时器：起点处依次展示卡片，用 ref 管理以便随时取消
  const introTimersRef = useRef<number[]>([])
  const clearIntro = useCallback(() => {
    introTimersRef.current.forEach((t) => window.clearTimeout(t))
    introTimersRef.current = []
  }, [])

  // 走「进入序列」：回到起点 → 镜头移到起点节点本身并依次停顿展示起点处各节点卡片 → 开始播放。
  // 加载时、点播放从头/重播时都走它；中途暂停后续播不走（见 handleTogglePlay），避免重复展示。
  // 起点不在 flat 里（这条轨迹起点后全是虚线、首个实线点在很久之后的另一地点），
  // 不能靠 positionAt 居中，必须用按节点居中的 focusNode。
  const runIntro = useCallback(() => {
    clearIntro()
    passedNodesRef.current = new Set()
    pauseUntilRef.current = 0
    playTimeRef.current = trackStart
    setPlayTime(trackStart)
    setPlaying(false)
    if (!(trackEnd > trackStart)) return
    const indices = introNodeIndices.length ? introNodeIndices : [0]
    // 立即把镜头移到起点并显示起点卡
    if (mapRef.current) mapRef.current.focusNode(indices[0])
    // 其余同刻节点（如首个停留点）依次停顿展示，每张停 NODE_PAUSE_MS
    indices.slice(1).forEach((nodeIdx, k) => {
      introTimersRef.current.push(
        window.setTimeout(
          () => {
            if (mapRef.current) mapRef.current.focusNode(nodeIdx)
          },
          (k + 1) * NODE_PAUSE_MS
        )
      )
    })
    // 卡片全部展示完 → 开始时间驱动播放
    introTimersRef.current.push(
      window.setTimeout(() => setPlaying(true), indices.length * NODE_PAUSE_MS)
    )
  }, [clearIntro, trackStart, trackEnd, introNodeIndices])

  // 轨迹加载/切换 → 走「进入序列」后自动播放
  useEffect(() => {
    runIntro()
    return clearIntro
  }, [runIntro, clearIntro])

  // 播放循环：rAF 时间驱动（60fps），经过节点停顿，到末尾停在终点
  useEffect(() => {
    if (!playing) return
    const span = trackEnd - trackStart
    if (span <= 0) return
    let raf = 0
    let last = 0
    const tick = (now: number) => {
      // 已暂停：立即终止循环，不再排下一帧
      // （兜底 effect 清理是 passive、晚于绘制，期间已排队的帧仍会推进，导致「暂停不了」）
      if (!playingRef.current) return
      if (!last) last = now
      // 节点停顿中
      if (pauseUntilRef.current > now) {
        last = now
        raf = window.requestAnimationFrame(tick)
        return
      }
      const delta = now - last
      last = now
      const prev = playTimeRef.current
      let next = prev + (span / BASE_PLAY_MS) * delta * speed
      // 经过尚未停顿过的节点 → 停在节点并停顿
      const crossed = nodeTimes.find(
        (t) => t > prev && t <= next && !passedNodesRef.current.has(t)
      )
      if (crossed != null) {
        passedNodesRef.current.add(crossed)
        pauseUntilRef.current = now + NODE_PAUSE_MS
        next = crossed
      } else if (next >= trackEnd) {
        next = trackEnd
      }
      playTimeRef.current = next
      setPlayTime(next)
      raf = window.requestAnimationFrame(tick)
    }
    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [playing, speed, trackStart, trackEnd, nodeTimes])

  // 到达终点自动暂停
  useEffect(() => {
    if (playing && trackEnd > trackStart && playTime >= trackEnd) {
      setPlaying(false)
    }
  }, [playTime, trackEnd, trackStart, playing])

  const handleTogglePlay = useCallback(() => {
    if (playing) {
      // 暂停：同时取消可能在跑的「进入序列」
      clearIntro()
      setPlaying(false)
      return
    }
    // 从头/末尾点播放 → 走「进入序列」重新展示起点卡片；中途暂停后续播 → 直接续播
    if (playTime >= trackEnd || playTime <= trackStart) {
      runIntro()
    } else {
      setPlaying(true)
    }
  }, [playing, playTime, trackStart, trackEnd, runIntro, clearIntro])

  // 拖动中：暂停播放（停 RAF，避免播放推进与拖动叠加变快），只更新时刻
  const handleScrub = useCallback((t: number) => {
    scrubbingRef.current = true
    setPlaying(false)
    pauseUntilRef.current = 0
    setPlayTime(t)
  }, [])

  // 松手：停在该位置并保持暂停（不自动续播，需手动点播放）；重置已停顿集合
  const handleScrubEnd = useCallback((t: number) => {
    scrubbingRef.current = false
    pauseUntilRef.current = 0
    passedNodesRef.current = new Set()
    setPlayTime(t)
  }, [])

  const handleSkip = useCallback(() => {
    setPlayTime(trackEnd)
    setPlaying(false)
  }, [trackEnd])

  const handleBack = useCallback(() => {
    navigate('/member/track')
  }, [navigate])

  const handleChangeMember = useCallback(
    (uid: string) => {
      replaceReplay(navigate, uid, dateStr)
    },
    [navigate, dateStr]
  )

  const handleChangeDate = useCallback(
    (d: dayjs.Dayjs | null) => {
      if (d) replaceReplay(navigate, userId, d.format('YYYY-MM-DD'))
    },
    [navigate, userId]
  )

  const handleSelectNode = useCallback((nodeIndex: number) => {
    if (mapRef.current) mapRef.current.focusNode(nodeIndex)
  }, [])

  const handleClickMapPoi = useCallback(() => setPoiOpen(true), [])
  const handleClickSettings = useCallback(() => setSettingsOpen(true), [])

  const disabledDate = (current: dayjs.Dayjs | null) =>
    !!current && current > dayjs().endOf('day')

  return (
    <Layout className="member-track-container">
      <div className="replay-topbar">
        <span className="replay-topbar__back" onClick={handleBack}>
          <LeftOutlined /> 返回
        </span>
        <DatePicker
          value={dateMoment}
          allowClear={false}
          disabledDate={disabledDate}
          onChange={handleChangeDate}
        />
        <span className="replay-topbar__spacer" />
        {isMock && (
          <Tag color="orange" className="replay-topbar__mock">
            mock 数据
          </Tag>
        )}
        <Button type="primary" ghost onClick={handleClickMapPoi}>
          地图点位
        </Button>
        <Button type="primary" ghost onClick={handleClickSettings}>
          设置
        </Button>
      </div>

      <Content className="member-track-content">
        <div className="member-track-main">
          <ReplayMap
            ref={mapRef}
            timeline={timeline}
            playTime={playTime}
            playing={playing}
            pois={mapPois}
            mapStyle={mapStyle}
          />
        </div>
        {/* 成员轨迹面板：贴右整块，从顶部工具行底部到地图底部；收起后只剩「轨迹」标签 */}
        <TrackSidePanel
          memberOptions={memberOptions}
          selectedUserId={userId}
          detail={detail}
          stats={stats}
          timeline={timeline}
          collapsed={sideCollapsed}
          onChangeMember={handleChangeMember}
          onToggleCollapse={() => setSideCollapsed((v) => !v)}
          onSelectNode={handleSelectNode}
        />
      </Content>
      {/* 底部浮条：地图样式卡片 + 播放控件卡片（两张独立卡片，中间留隙） */}
      <div
        className={`replay-bottom ${
          sideCollapsed ? 'replay-bottom--wide' : ''
        }`}
      >
        <div className="replay-bottom__box">
          <MapStyleSelect value={mapStyle} onChange={onChangeMapStyle} />
        </div>
        <ReplayControl
          playing={playing}
          speed={speed}
          rangeStart={trackStart}
          rangeEnd={trackEnd}
          playTime={playTime}
          onTogglePlay={handleTogglePlay}
          onChangeSpeed={setSpeed}
          onScrub={handleScrub}
          onScrubEnd={handleScrubEnd}
          onSkip={handleSkip}
        />
      </div>
      <PoiManager
        visible={poiOpen}
        groupId={groupId}
        onClose={() => {
          setPoiOpen(false)
          refreshPois()
        }}
        onFocusPoi={(lng, lat) => {
          // 关闭弹窗、刷新地图点位、居中放大到该点位
          setPoiOpen(false)
          refreshPois()
          if (mapRef.current) mapRef.current.centerTo(lng, lat)
        }}
      />
      <LocationSettings
        visible={settingsOpen}
        groupId={groupId}
        onClose={() => setSettingsOpen(false)}
      />
    </Layout>
  )
}

export default TrackReplay
